import React, { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import EvidenceReferenceView from "../../components/EvidenceReferenceView";
import "../../styles/DashboardHome.css";
import "../../styles/Tables.css";
import "../../styles/CertificationWorkflow.css";
import {
  fetchEmsStageReadinessApi,
  signoffEmsStageReadinessApi,
  updateEmsStageReadinessApi,
} from "../../lib/api";

function stageCompletionPct(stageObj) {
  const items = Array.isArray(stageObj?.checklist) ? stageObj.checklist : [];
  if (!items.length) return 0;
  const done = items.filter((x) => Boolean(x?.done)).length;
  return Math.round((done / items.length) * 1000) / 10;
}

export default function EMSCertificationWorkflow() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState(null);
  const role = String(localStorage.getItem("role") || "").toLowerCase();
  const canEdit = role === "admin";

  const loadData = async () => {
    try {
      setLoading(true);
      const payload = await fetchEmsStageReadinessApi();
      setData(payload || null);
    } catch (err) {
      toast.error(err?.message || "Failed to load certification workflow");
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const stage1Pct = useMemo(() => stageCompletionPct(data?.stage_1), [data]);
  const stage2Pct = useMemo(() => stageCompletionPct(data?.stage_2), [data]);

  const updateChecklistField = (stageKey, itemId, key, value) => {
    setData((prev) => {
      const next = { ...(prev || {}) };
      const stage = { ...(next?.[stageKey] || {}) };
      const checklist = Array.isArray(stage.checklist) ? stage.checklist.map((it) => ({ ...it })) : [];
      const idx = checklist.findIndex((it) => String(it?.id) === String(itemId));
      if (idx >= 0) checklist[idx][key] = value;
      stage.checklist = checklist;
      next[stageKey] = stage;
      return next;
    });
  };

  const saveStage = async (stageKey) => {
    if (!canEdit) {
      toast.error("Only admin can update stage readiness");
      return;
    }
    try {
      setSaving(true);
      const payload = { [stageKey]: data?.[stageKey] || {} };
      const updated = await updateEmsStageReadinessApi(payload);
      setData(updated || null);
      toast.success(`${stageKey} saved`);
    } catch (err) {
      toast.error(err?.message || "Failed to save stage data");
    } finally {
      setSaving(false);
    }
  };

  const handleSignoff = async (stageKey, signoff) => {
    if (!canEdit) {
      toast.error("Only admin can sign off stages");
      return;
    }
    try {
      setSaving(true);
      const notes = window.prompt(signoff ? `Sign off ${stageKey} notes:` : `Revoke ${stageKey} signoff notes:`, "") || "";
      const updated = await signoffEmsStageReadinessApi({
        stage: stageKey,
        signoff,
        notes,
      });
      setData(updated || null);
      toast.success(signoff ? `${stageKey} signed off` : `${stageKey} signoff revoked`);
    } catch (err) {
      toast.error(err?.message || "Failed to update stage signoff");
    } finally {
      setSaving(false);
    }
  };

  const renderStageCard = (stageKey, label) => {
    const stage = data?.[stageKey] || {};
    const checklist = Array.isArray(stage?.checklist) ? stage.checklist : [];
    const pct = stageKey === "stage_1" ? stage1Pct : stage2Pct;

    return (
      <section className="table-card cert-workflow-stage-card">
        <div className="dashboard-header cert-workflow-stage-header">
          <div className="cert-workflow-stage-copy">
            <h2>{label}</h2>
            <p className="subtitle cert-workflow-stage-subtitle">
              Required completion: {Number(stage?.required_completion_pct || 100)}% | Current: {pct}%
            </p>
          </div>
          <div className="dashboard-header-actions cert-workflow-stage-actions">
            <button className="btn-secondary" onClick={() => saveStage(stageKey)} disabled={!canEdit || saving || loading}>
              {saving ? "Saving..." : "Save stage"}
            </button>
            {Boolean(stage?.signed_off) ? (
              <button className="btn-secondary" onClick={() => handleSignoff(stageKey, false)} disabled={!canEdit || saving || loading}>
                Revoke signoff
              </button>
            ) : (
              <button className="btn-primary" onClick={() => handleSignoff(stageKey, true)} disabled={!canEdit || saving || loading}>
                Sign off stage
              </button>
            )}
          </div>
        </div>

        <div className="cert-workflow-table-wrap">
          <table>
          <thead>
            <tr>
              <th>Done</th>
              <th>Checklist Item</th>
              <th>Evidence</th>
            </tr>
          </thead>
          <tbody>
            {checklist.length === 0 ? (
              <tr><td colSpan={3} className="table-empty">No checklist items configured.</td></tr>
            ) : (
              checklist.map((it) => (
                <tr key={it.id || it.title}>
                  <td>
                    <input
                      type="checkbox"
                      checked={Boolean(it.done)}
                      disabled={!canEdit || saving}
                      onChange={(e) => updateChecklistField(stageKey, it.id, "done", e.target.checked)}
                    />
                  </td>
                  <td>{it.title || "-"}</td>
                  <td>
                    {canEdit ? (
                      <input
                        value={String(it.evidence || "")}
                        onChange={(e) => updateChecklistField(stageKey, it.id, "evidence", e.target.value)}
                        disabled={saving}
                      />
                    ) : <EvidenceReferenceView value={it.evidence} />}
                  </td>
                </tr>
              ))
            )}
          </tbody>
          </table>
        </div>

        <div className="kpi-sub cert-workflow-signoff-meta">
          Signed off: {Boolean(stage?.signed_off) ? "Yes" : "No"}
          {stage?.signed_off_by ? ` | by ${stage.signed_off_by}` : ""}
          {stage?.signed_off_at ? ` at ${stage.signed_off_at}` : ""}
        </div>
      </section>
    );
  };

  return (
    <div className="dashboard-page cert-workflow-page">
      <div className="dashboard-header cert-workflow-page-header">
        <div>
          <h1>Certification Workflow</h1>
          <p className="subtitle cert-workflow-page-subtitle">Stage 1 and Stage 2 readiness checklists with sign-off gates and evidence links.</p>
        </div>
      </div>

      <div className="summary-row cert-workflow-summary-row">
        <div className="kpi-card cert-workflow-kpi-card">
          <div className="kpi-label">Stage 1 completion</div>
          <h2>{loading ? "-" : `${stage1Pct}%`}</h2>
        </div>
        <div className="kpi-card cert-workflow-kpi-card">
          <div className="kpi-label">Stage 2 completion</div>
          <h2>{loading ? "-" : `${stage2Pct}%`}</h2>
        </div>
        <div className="kpi-card cert-workflow-kpi-card">
          <div className="kpi-label">Mock audit cadence</div>
          <h2>{loading ? "-" : String(data?.mock_audit_cadence || "-")}</h2>
        </div>
      </div>

      {renderStageCard("stage_1", "Stage 1")}
      {renderStageCard("stage_2", "Stage 2")}
    </div>
  );
}
